// Import directly from subpath to avoid circular dependency:
// '@blocksuite/affine-block-surface' barrel re-exports crud-extension.ts
// which imports moveToTrashcan from '@blocksuite/affine-block-streamlain'
// creating a cycle back to this package.
import {
  EdgelessClipboardConfig,
  type PreparedEdgelessBlock,
} from '@blocksuite/affine-block-surface/clipboard-config';
import { type BlockSnapshot } from '@blocksuite/store';

/**
 * Base implementation for StreamLain edgeless clipboard configs.
 * All StreamLain blocks use the same paste logic: insert as child of root.
 */
async function pasteStreamLainBlock(
  config: EdgelessClipboardConfig,
  snapshot: BlockSnapshot,
  blockType: string
): Promise<PreparedEdgelessBlock | null> {
  const oldId = snapshot.id;

  delete snapshot.props.index;
  if (!snapshot.props.xywh) {
    console.error(
      `${blockType} block(id: ${oldId}) does not have xywh property`
    );
    return null;
  }

  const prepared = await config.prepareSnapshotPaste(
    snapshot,
    config.std.store,
    config.std.store.root!.id
  );
  if (!prepared)
    console.error(`Failed to paste ${blockType} block(id: ${oldId})`);
  return prepared;
}

export class EdgelessClipboardAutomationConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:automation';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    if (snapshot.props.executionKind === 'cleanup') {
      snapshot.props.triggerConfig = {
        ...(snapshot.props.triggerConfig as Record<string, unknown>),
        enabled: false,
      };
      snapshot.props.lastRunAt = null;
      snapshot.props.lastRunStatus = null;
    }
    return pasteStreamLainBlock(this, snapshot, 'Automation');
  }
}

export class EdgelessClipboardElsterConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:elster';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    return pasteStreamLainBlock(this, snapshot, 'Elster');
  }
}

export class EdgelessClipboardGhostConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:ghost';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    return pasteStreamLainBlock(this, snapshot, 'Ghost');
  }
}

export class EdgelessClipboardTutorialCardConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:tutorial-card';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    return pasteStreamLainBlock(this, snapshot, 'TutorialCard');
  }
}

export class EdgelessClipboardStickyNoteConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:sticky-note';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    return pasteStreamLainBlock(this, snapshot, 'StickyNote');
  }
}

export class EdgelessClipboardBrushHeadingConfig extends EdgelessClipboardConfig {
  static override readonly key = 'affine:brush-heading';

  override async prepareBlock(
    snapshot: BlockSnapshot
  ): Promise<PreparedEdgelessBlock | null> {
    return pasteStreamLainBlock(this, snapshot, 'BrushHeading');
  }
}
